/** Shared campaign performance metrics shape used across campaign tables. */
export interface CampaignPerformanceMetrics {
  impressions?: string;
  opens?: string;
  clicks?: string;
  conversions?: string;
  revenue?: string;
  total_revenue?: string;
}

export interface CampaignPerformanceSource {
  performance?: CampaignPerformanceMetrics | null;
  tracking?: CampaignPerformanceMetrics | null;
}

export function getCampaignPerformanceData(
  source: CampaignPerformanceSource | null | undefined
): CampaignPerformanceMetrics | null {
  if (!source) {
    return null;
  }
  if (source.tracking) {
    return source.tracking;
  }
  if (source.performance) {
    return source.performance;
  }
  return null;
}

export function getCampaignImpressionsAndOpens(data: CampaignPerformanceMetrics | null): string {
  if (!data) {
    return '0';
  }

  const impressions = parseInt(data.impressions || '0', 10);
  const opens = parseInt(data.opens || '0', 10);
  return (impressions + opens).toLocaleString('en-US');
}

export function getCampaignClicks(data: CampaignPerformanceMetrics | null): string {
  if (!data) {
    return '0';
  }

  return parseInt(data.clicks || '0', 10).toLocaleString('en-US');
}

export function getCampaignClickPercentage(data: CampaignPerformanceMetrics | null): string {
  if (!data) {
    return '0';
  }

  const clicks = parseInt(data.clicks || '0', 10);
  const impressions = parseInt(data.impressions || '0', 10);
  const opens = parseInt(data.opens || '0', 10);
  const totalImpressionsAndOpens = impressions + opens;

  if (totalImpressionsAndOpens === 0) {
    return '0';
  }

  return Math.round((clicks / totalImpressionsAndOpens) * 100).toString();
}

export function getCampaignConversions(data: CampaignPerformanceMetrics | null): string {
  if (!data) {
    return '0';
  }

  return parseInt(data.conversions || '0', 10).toLocaleString('en-US');
}

export function getCampaignConversionPercentage(data: CampaignPerformanceMetrics | null): string {
  if (!data) {
    return '0';
  }

  const conversions = parseInt(data.conversions || '0', 10);
  const impressions = parseInt(data.impressions || '0', 10);
  const opens = parseInt(data.opens || '0', 10);
  const totalImpressionsAndOpens = impressions + opens;

  if (totalImpressionsAndOpens === 0) {
    return '0';
  }

  return Math.round((conversions / totalImpressionsAndOpens) * 100).toString();
}

export function getCampaignRevenue(data: CampaignPerformanceMetrics | null): string {
  if (!data) {
    return '$0';
  }

  const revenue = parseFloat(data.total_revenue || data.revenue || '0');
  if (revenue % 1 === 0) {
    return `${revenue.toLocaleString('en-US')}`;
  }
  return `${revenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
